// src/components/Header/SearchModal.tsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Search,
  Clock,
  Star,
  Users,
  FileText,
  Hash,
  Zap,
  CheckSquare,
  Folder,
  Rocket,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { fetchBoards } from '../../store/boardSlice';
import { fetchWorkspaces } from '../../store/workspaceSlice';
import { fetchTasks } from '../../store/taskSlice';
import { boardsApi } from '../../api/endpoints/boardsApi';
import { TaskPriority } from '../../types/common';
import './Modals.scss';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResultItem {
  id: string;
  category: 'task' | 'board' | 'workspace' | 'sprint' | 'epic' | 'person';
  title: string;
  subtitle?: string;
  badge?: string;
  badgeColor?: string;
  icon: React.ReactNode;
  url: string;
}

const RECENT_SEARCHES_KEY = 'agile_edge_recent_searches';

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [savedSearches, setSavedSearches] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [assignedToMeOnly, setAssignedToMeOnly] = useState(false);
  const [boardMembers, setBoardMembers] = useState<any[]>([]);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Redux data
  const { user } = useAppSelector((state) => state.auth);
  const { boards, currentBoard } = useAppSelector((state) => state.boards);
  const { workspaces } = useAppSelector((state) => state.workspaces);
  const { tasks } = useAppSelector((state) => state.tasks);
  const { sprints } = useAppSelector((state) => state.sprints);
  const { epics } = useAppSelector((state) => state.epics);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to parse recent searches', e);
    }
  }, []);

  // Fetch real system entities on modal open if needed
  useEffect(() => {
    if (isOpen) {
      if (boards.length === 0) dispatch(fetchBoards());
      if (workspaces.length === 0) dispatch(fetchWorkspaces());
      if (currentBoard?.id && tasks.length === 0) {
        dispatch(fetchTasks({ boardId: currentBoard.id }));
      }
      if (currentBoard?.id) {
        boardsApi.getBoardAccess(currentBoard.id).then((access) => {
          setBoardMembers(access.members || []);
        }).catch(() => { });
      }

      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, dispatch, boards.length, workspaces.length, tasks.length, currentBoard?.id]);

  const saveRecentSearch = (query: string) => {
    if (!query.trim()) return;
    const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(0, 6);
    setRecentSearches(updated);
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save recent search', e);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      saveRecentSearch(searchQuery.trim());
    }
  };

  const handleResultClick = (item: SearchResultItem) => {
    if (searchQuery.trim()) {
      saveRecentSearch(searchQuery.trim());
    }
    onClose();
    navigate(item.url);
  };

  const handleSaveSearch = (query: string) => {
    if (!query.trim() || savedSearches.includes(query)) return;
    setSavedSearches([...savedSearches, query]);
  };

  // Compile real search results
  const allSearchResults = useMemo<SearchResultItem[]>(() => {
    const query = searchQuery.trim().toLowerCase();
    const results: SearchResultItem[] = [];

    // Filter tasks
    tasks.forEach((task) => {
      if (assignedToMeOnly && String(task.assigneeId) !== String(user?.id)) {
        return;
      }

      const matchTitle = task.title.toLowerCase().includes(query);
      const matchKey = task.key?.toLowerCase().includes(query);
      const matchDesc = task.description?.toLowerCase().includes(query);
      const matchTag = task.tags?.some((t) => t.toLowerCase().includes(query));

      if (!query || matchTitle || matchKey || matchDesc || matchTag) {
        const board = boards.find((b) => String(b.id) === String(task.boardId));
        results.push({
          id: `task-${task.id}`,
          category: 'task',
          title: task.title,
          subtitle: `${task.key ? `[${task.key}] ` : ''}${board?.name ? `in ${board.name}` : ''} • Status: ${task.status}`,
          badge: task.priority ? String(task.priority).toUpperCase() : undefined,
          badgeColor: task.priority === TaskPriority.URGENT || task.priority === TaskPriority.HIGH || String(task.priority).toUpperCase() === 'URGENT' || String(task.priority).toUpperCase() === 'HIGH' ? '#e74c3c' : '#4A90E2',
          icon: <CheckSquare size={16} color="#6366f1" />,
          url: `/boards/${task.boardId}?taskId=${task.id}`,
        });
      }
    });

    if (assignedToMeOnly && !query) {
      return results;
    }

    if (!query) return results;

    // Filter boards
    boards.forEach((board) => {
      if (
        board.name.toLowerCase().includes(query) ||
        board.key?.toLowerCase().includes(query) ||
        board.description?.toLowerCase().includes(query)
      ) {
        results.push({
          id: `board-${board.id}`,
          category: 'board',
          title: board.name,
          subtitle: `${board.key ? `[${board.key}] ` : ''}Board`,
          badge: 'BOARD',
          badgeColor: '#0073ea',
          icon: <Hash size={16} color="#0073ea" />,
          url: `/boards/${board.id}`,
        });
      }
    });

    // Filter workspaces
    workspaces.forEach((ws) => {
      if (ws.name.toLowerCase().includes(query) || ws.description?.toLowerCase().includes(query)) {
        results.push({
          id: `ws-${ws.id}`,
          category: 'workspace',
          title: ws.name,
          subtitle: 'Workspace',
          badge: 'WORKSPACE',
          badgeColor: '#22c58c',
          icon: <Folder size={16} color="#22c58c" />,
          url: `/workspace/${ws.id}`,
        });
      }
    });

    // Filter sprints
    sprints.forEach((sprint) => {
      if (sprint.name.toLowerCase().includes(query) || sprint.goal?.toLowerCase().includes(query)) {
        results.push({
          id: `sprint-${sprint.id}`,
          category: 'sprint',
          title: sprint.name,
          subtitle: `${sprint.status} Sprint${sprint.goal ? ` • Goal: ${sprint.goal}` : ''}`,
          badge: sprint.status,
          badgeColor: sprint.status === 'ACTIVE' ? '#0073ea' : '#8b7fd6',
          icon: <Rocket size={16} color="#8b7fd6" />,
          url: `/boards/${sprint.boardId}/sprint-board`,
        });
      }
    });

    // Filter epics
    epics.forEach((epic) => {
      if (epic.name.toLowerCase().includes(query) || epic.description?.toLowerCase().includes(query)) {
        results.push({
          id: `epic-${epic.id}`,
          category: 'epic',
          title: epic.name,
          subtitle: `Epic`,
          badge: 'EPIC',
          badgeColor: epic.color || '#9b59b6',
          icon: <Zap size={16} color={epic.color || '#9b59b6'} />,
          url: `/boards/${epic.boardId}`,
        });
      }
    });

    // Filter people
    boardMembers.forEach((member) => {
      const name = member.fullName || member.userName || '';
      const email = member.email || '';
      if (name.toLowerCase().includes(query) || email.toLowerCase().includes(query)) {
        results.push({
          id: `user-${member.id}`,
          category: 'person',
          title: name,
          subtitle: `${email}${member.role ? ` • ${member.role}` : ''}`,
          badge: 'MEMBER',
          badgeColor: '#f39c12',
          icon: <Users size={16} color="#f39c12" />,
          url: '/team',
        });
      }
    });

    return results;
  }, [searchQuery, tasks, boards, workspaces, sprints, epics, boardMembers, assignedToMeOnly, user?.id]);

  // Filtered by tab
  const filteredResults = useMemo(() => {
    if (activeTab === 0) return allSearchResults; // All
    if (activeTab === 1) return allSearchResults.filter((r) => r.category === 'task');
    if (activeTab === 2) return allSearchResults.filter((r) => r.category === 'board');
    if (activeTab === 3) return allSearchResults.filter((r) => r.category === 'workspace');
    if (activeTab === 4) return allSearchResults.filter((r) => r.category === 'sprint' || r.category === 'epic');
    if (activeTab === 5) return allSearchResults.filter((r) => r.category === 'person');
    return allSearchResults;
  }, [allSearchResults, activeTab]);

  const tabs = [
    { label: 'All', count: allSearchResults.length },
    { label: 'Tasks', count: allSearchResults.filter((r) => r.category === 'task').length },
    { label: 'Boards', count: allSearchResults.filter((r) => r.category === 'board').length },
    { label: 'Workspaces', count: allSearchResults.filter((r) => r.category === 'workspace').length },
    { label: 'Sprints & Epics', count: allSearchResults.filter((r) => r.category === 'sprint' || r.category === 'epic').length },
    { label: 'People', count: allSearchResults.filter((r) => r.category === 'person').length },
  ];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay search-modal-overlay" onClick={onClose}>
      <div className="modal-content search-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header Search Input */}
        <Box className="search-modal-header">
          <form className="search-input-container" onSubmit={handleSearchSubmit}>
            <TextField
              inputRef={searchInputRef}
              fullWidth
              placeholder="Search tasks, boards, workspaces, sprints, epics..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} style={{ color: '#0073ea' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchQuery && (
                      <IconButton onClick={() => setSearchQuery('')} size="small" sx={{ mr: 0.5 }}>
                        <X size={16} />
                      </IconButton>
                    )}
                    {searchQuery.trim() && (
                      <Tooltip title="Save Search">
                        <IconButton onClick={() => handleSaveSearch(searchQuery.trim())} size="small" sx={{ color: '#ffb300', mr: 0.5 }}>
                          <Star size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton onClick={onClose} edge="end" size="small">
                      <X size={20} />
                    </IconButton>
                  </InputAdornment>
                ),
                style: { fontSize: '15px', fontWeight: 500 },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '2px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0073ea',
                  },
                },
              }}
            />
          </form>
        </Box>

        {/* Category Tabs */}
        <Box className="search-tabs-container" sx={{ position: 'relative' }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: '1px solid #e2e8f0',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 600,
                color: '#64748b',
                minHeight: '44px',
                '&.Mui-selected': {
                  color: '#0073ea',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#0073ea',
                height: '3px',
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <span>{tab.label}</span>
                    {tab.count > 0 && (
                      <Box
                        component="span"
                        sx={{
                          background: index === activeTab ? 'rgba(0,115,234,0.12)' : '#f1f5f9',
                          color: index === activeTab ? '#0073ea' : '#64748b',
                          padding: '1px 7px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 700,
                        }}
                      >
                        {tab.count}
                      </Box>
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Modal Body */}
        <div className="search-modal-body">
          {!searchQuery && !assignedToMeOnly ? (
            <div className="search-initial-state">
              <div className="search-sections">
                {/* Related to me */}
                <div className="search-section">
                  <div className="section-header">
                    <Users size={16} color="#0073ea" />
                    <span>Quick Filters</span>
                  </div>
                  <Box className="quick-filters" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={assignedToMeOnly}
                          onChange={(e) => setAssignedToMeOnly(e.target.checked)}
                        />
                      }
                      label="Assigned to me"
                      sx={{
                        margin: 0,
                        '& .MuiFormControlLabel-label': { fontSize: '13px', fontWeight: 500, color: '#334155' },
                      }}
                    />
                  </Box>
                </div>

                {/* Saved Searches */}
                {savedSearches.length > 0 && (
                  <div className="search-section">
                    <div className="section-header">
                      <Star size={16} color="#ffb300" />
                      <span>Saved Searches</span>
                    </div>
                    <div className="saved-searches" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {savedSearches.map((search, index) => (
                        <Chip
                          key={index}
                          label={search}
                          onClick={() => setSearchQuery(search)}
                          onDelete={() => setSavedSearches(savedSearches.filter((s) => s !== search))}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Searches */}
                <div className="search-section">
                  <div className="section-header">
                    <Clock size={16} color="#64748b" />
                    <span>Recent Searches</span>
                  </div>
                  <div className="recent-searches">
                    {recentSearches.length === 0 ? (
                      <p className="empty-state">
                        Start typing to search tasks, boards, workspaces, sprints, and people across your organization.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {recentSearches.map((search, index) => (
                          <Chip
                            key={index}
                            icon={<Search size={12} />}
                            label={search}
                            onClick={() => setSearchQuery(search)}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500, bgcolor: '#f8fafc' }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Search Tip */}
              <div className="quick-search-tip">
                <div className="tip-header">
                  <Zap size={16} color="#6366f1" />
                  <span>Shortcut</span>
                </div>
                <div className="keyboard-shortcut">
                  <span className="key">Ctrl</span>
                  <span className="plus">+</span>
                  <span className="key">K</span>
                </div>
                <p className="tip-description">Press Ctrl+K anytime to open global search instantly.</p>
              </div>
            </div>
          ) : (
            <div className="search-results">
              {/* Active Filter Bar & Back Navigation */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'space-between',
                  mb: 2,
                  pb: 1.25,
                  borderBottom: '1px solid #e2e8f0',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<ArrowLeft size={16} />}
                    onClick={() => {
                      setAssignedToMeOnly(false);
                      setSearchQuery('');
                      setActiveTab(0);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      color: '#475569',
                      bgcolor: '#f1f5f9',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.5,
                      '&:hover': { color: '#0073ea', bgcolor: 'rgba(0,115,234,0.1)' },
                    }}
                  >
                    Back to Quick Filters
                  </Button>

                  {assignedToMeOnly && (
                    <Chip
                      label="Assigned to me"
                      onDelete={() => setAssignedToMeOnly(false)}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  )}
                </Box>

                {(assignedToMeOnly || searchQuery) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setAssignedToMeOnly(false);
                      setSearchQuery('');
                      setActiveTab(0);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.775rem',
                      fontWeight: 600,
                      color: '#94a3b8',
                      '&:hover': { color: '#e74c3c' },
                    }}
                  >
                    Clear all
                  </Button>
                )}
              </Box>

              <div className="results-container">
                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : filteredResults.length > 0 ? (
                  <div className="results-list" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {filteredResults.map((result) => (
                      <div
                        key={result.id}
                        className="search-result-item"
                        onClick={() => handleResultClick(result)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          gap: '12px',
                          background: '#ffffff',
                          border: '1px solid #f1f5f9',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <div className="result-icon" style={{ flexShrink: 0 }}>
                          {result.icon}
                        </div>
                        <div className="result-content" style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className="result-title"
                            style={{ fontWeight: 600, fontSize: '0.9rem', color: '#0f172a' }}
                          >
                            {result.title}
                          </div>
                          {result.subtitle && (
                            <div
                              className="result-description"
                              style={{ fontSize: '0.775rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {result.subtitle}
                            </div>
                          )}
                        </div>
                        {result.badge && (
                          <Chip
                            label={result.badge}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.675rem',
                              fontWeight: 700,
                              bgcolor: result.badgeColor ? `${result.badgeColor}18` : 'rgba(0,115,234,0.1)',
                              color: result.badgeColor || '#0073ea',
                              border: `1px solid ${result.badgeColor || '#0073ea'}40`,
                            }}
                          />
                        )}
                        <ArrowRight size={16} color="#94a3b8" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-results" style={{ textAlign: 'center', padding: '36px 16px' }}>
                    <p style={{ fontWeight: 600, color: '#334155', margin: '0 0 6px 0' }}>
                      No matching results found for "{searchQuery}"
                    </p>
                    <p className="suggestion" style={{ fontSize: '0.825rem', color: '#64748b', margin: 0 }}>
                      Try checking spelling or searching for board names, task keys (e.g. 9B-10), or keywords.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};